<script setup lang="ts">
import imageCompression from 'browser-image-compression'

/**
 * 表情包管理组件
 * 用于在联系人设置中管理表情包
 */
import { Button } from '@proj-airi/ui'
import { computed, ref } from 'vue'

import { useStickerAutoName } from '../../composables/useStickerAutoName'
import { convertHttpImageToDataUrl } from '../../stores/chat-helpers'
import { isSafeStickerUrl, STICKER_COMPRESS_THRESHOLD, STICKER_COMPRESSION_OPTIONS, useStickersStore } from '../../stores/stickers'
import { showError, showSuccess } from '../../utils/toast'
import { ConfirmDialog } from '../Chat'

const props = defineProps<{
  packId: string
}>()

const stickersStore = useStickersStore()
const { isAvailable: isVisionAvailable, autoName, batchAutoName, cancelNaming, isNaming, namingProgress, namingTotal, lastError: visionError } = useStickerAutoName()

// AI 命名开关
const enableAiNaming = ref(true)

// 文件上传相关
const imageFileInput = ref<HTMLInputElement | null>(null)
const zipFileInput = ref<HTMLInputElement | null>(null)

// 当前 Pack 的表情包
const contactStickers = computed(() => {
  const pack = stickersStore.getPackById(props.packId)
  if (!pack)
    return []
  return [...pack.stickers].sort((a, b) => b.order - a.order)
})

// UI 状态
const showAddDialog = ref(false)
const showBulkImportDialog = ref(false)
const showExportDialog = ref(false)
const showRenameDialog = ref(false)
const importInput = ref('')
const newStickerName = ref('')
const newStickerUrl = ref('')
const addMode = ref<'url' | 'image'>('url') // 添加模式：URL或图片
const bulkMode = ref<'text' | 'zip'>('text') // 批量导入模式：文本或ZIP
const isImporting = ref(false)
const importResult = ref<{ success: number, failed: number, skipped: number } | null>(null)
// URL 批量导入专用的中止控制器
const bulkImportAbort = ref<AbortController | null>(null)
// 输入解析问题提示（JSON/文本解析失败但没抛异常时给用户看）
const importParseIssue = ref<string | null>(null)
const selectedStickers = ref<Set<string>>(new Set())
const isEditMode = ref(false)
const renamingSticker = ref<{ id: string, name: string } | null>(null)
const isAutoNaming = ref(false)

// 文件处理进度追踪
const processingProgress = ref(0)
const processingTotal = ref(0)
const isProcessing = computed(() => processingProgress.value > 0 && processingProgress.value < processingTotal.value)

// 头部更多菜单
const showMoreMenu = ref(false)

// ZIP 已选文件名
const uploadedZipFileName = ref<string | null>(null)

// 确认对话框
const showDeleteConfirm = ref(false)
const showBatchDeleteConfirm = ref(false)
const deletingStickerId = ref<string | null>(null)

function closeBulkImportDialog() {
  if (isImporting.value || isNaming.value)
    return
  showBulkImportDialog.value = false
  importInput.value = ''
  importResult.value = null
  importParseIssue.value = null
  bulkMode.value = 'text'
  uploadedZipFileName.value = null
}

function cancelImport() {
  // 取消当前进行中的 URL 批量下载（仅文本/URL 导入路径使用）
  bulkImportAbort.value?.abort()
}

function closeAddDialog() {
  showAddDialog.value = false
  newStickerName.value = ''
  newStickerUrl.value = ''
  addMode.value = 'url'
}

async function normalizeStickerUrlForImport(url: string, signal?: AbortSignal): Promise<string | null> {
  if (signal?.aborted)
    return null
  // 双层防御：即便上游 isLikelyUrl 放行了 data:image/*，这里再用 stickers store 的白名单
  // 过滤掉 data:text/html 等非图片协议，防止将来重构时漏 check。
  if (!isSafeStickerUrl(url))
    return null
  if (url.startsWith('data:'))
    return url
  const dataUrl = await convertHttpImageToDataUrl(url)
  if (signal?.aborted)
    return null
  return dataUrl
}

async function runWithConcurrencyAbortable<T>(
  tasks: Array<() => Promise<T>>,
  limit: number,
  signal: AbortSignal,
  onStep?: () => void,
): Promise<T[]> {
  const results: T[] = Array.from({ length: tasks.length })
  let index = 0
  async function worker() {
    while (index < tasks.length) {
      if (signal.aborted)
        return
      const i = index++
      results[i] = await tasks[i]()
      // 已在途的 await 无法被取消信号中断，所以完成后再检查一次，
      // 避免"取消后进度条继续跳"的 UX 问题。
      if (signal.aborted)
        return
      onStep?.()
    }
  }
  await Promise.all(Array.from({ length: Math.min(limit, tasks.length) }, () => worker()))
  return results
}

// 批量导入
async function handleBulkImport() {
  if (!importInput.value.trim()) {
    showError('请输入要导入的内容')
    return
  }

  isImporting.value = true
  importResult.value = null
  importParseIssue.value = null
  const abort = new AbortController()
  bulkImportAbort.value = abort

  try {
    // 解析用户输入（JSON 数组或纯文本 name: url / 纯 URL 行）
    const parsed = parseBulkImportInput(importInput.value)
    const rawStickers = parsed.items

    if (rawStickers.length === 0) {
      importResult.value = { success: 0, failed: 0, skipped: 0 }
      importParseIssue.value = parsed.reason === 'json-shape'
        ? '未识别到任何有效条目，JSON 数组中每项需要包含 name 和 url 字段'
        : '未识别到任何有效条目，请检查每行格式（"名称: URL" 或纯 URL）'
    }
    else {
      // URL 预转换：http/https URL 并发下载成 data URI，失败跳过并计入 failed
      processingTotal.value = rawStickers.length
      processingProgress.value = 0
      const downloadResults: Array<{ name: string, url: string } | null> = Array.from({ length: rawStickers.length }, () => null)
      await runWithConcurrencyAbortable(
        rawStickers.map((s, idx) => async () => {
          const n = await normalizeStickerUrlForImport(s.url, abort.signal)
          if (n)
            downloadResults[idx] = { name: s.name, url: n }
        }),
        6,
        abort.signal,
        () => { processingProgress.value++ },
      )

      const normalized = downloadResults.filter((v): v is { name: string, url: string } => v !== null)
      const downloadFailed = rawStickers.length - normalized.length

      const result = normalized.length > 0
        ? stickersStore.bulkAddStickersToPack(normalized, props.packId)
        : { success: 0, failed: 0, skipped: 0 }
      importResult.value = {
        ...result,
        failed: result.failed + downloadFailed,
      }

      if (parsed.invalidLines > 0) {
        importParseIssue.value = `有 ${parsed.invalidLines} 行无法识别，已跳过`
      }
      if (abort.signal.aborted) {
        importParseIssue.value = '导入已取消'
      }
    }

    importInput.value = ''

    if (importResult.value.success > 0)
      showSuccess(`成功导入 ${importResult.value.success} 个表情包！`)

    setTimeout(() => {
      if (!showBulkImportDialog.value)
        return
      showBulkImportDialog.value = false
      importResult.value = null
      importParseIssue.value = null
    }, 2000)
  }
  catch (error) {
    showError(`导入失败: ${(error as Error).message}`)
  }
  finally {
    // 无论成功/异常/取消，进度状态一律在这里清零，保证 isProcessing 不残留
    processingTotal.value = 0
    processingProgress.value = 0
    isImporting.value = false
    bulkImportAbort.value = null
  }
}

interface BulkParseResult {
  items: Array<{ name: string, url: string }>
  invalidLines: number
  reason: 'empty' | 'json-shape' | 'text-no-valid-lines' | 'ok'
}

function isLikelyUrl(s: string): boolean {
  return /^(?:data:image\/|https?:\/\/|\/\/)/i.test(s)
}

function stickerNameFromUrl(url: string): string {
  if (url.startsWith('data:'))
    return '表情'
  try {
    const u = new URL(url)
    const lastRaw = u.pathname.split('/').filter(Boolean).pop() || u.hostname
    // URL 路径里可能是 %E7%8C%AB%E5%92%AA 之类的编码中文，decode 失败就原样返回
    let last: string
    try {
      last = decodeURIComponent(lastRaw)
    }
    catch {
      last = lastRaw
    }
    const stripped = last.replace(/\.(?:png|jpe?g|gif|webp|avif)$/i, '') || u.hostname
    // 限制最大长度，避免 path 段过长撑爆 UI
    return stripped.length > 40 ? `${stripped.slice(0, 40)}…` : stripped
  }
  catch {
    return '表情'
  }
}

function parseBulkImportInput(input: string): BulkParseResult {
  const trimmed = input.trim()
  if (!trimmed)
    return { items: [], invalidLines: 0, reason: 'empty' }

  // 1. JSON 数组
  try {
    const parsed = JSON.parse(trimmed)
    if (Array.isArray(parsed)) {
      const items = parsed
        .filter((s): s is { name: string, url: string } =>
          s && typeof s.name === 'string' && typeof s.url === 'string',
        )
        .map(s => ({ name: s.name.trim(), url: s.url.trim() }))
        .filter(s => s.name && s.url)
      return {
        items,
        invalidLines: Array.isArray(parsed) ? parsed.length - items.length : 0,
        reason: items.length > 0 ? 'ok' : 'json-shape',
      }
    }
  }
  catch {
    // 非 JSON，继续按文本格式解析
  }

  // 2. 文本模式：每行 "name: url" 或纯 URL
  const items: Array<{ name: string, url: string }> = []
  let invalid = 0
  for (const rawLine of trimmed.split('\n')) {
    const line = rawLine.trim()
    if (!line)
      continue
    // data: URL 含多个冒号，优先判断
    if (isLikelyUrl(line)) {
      items.push({ name: stickerNameFromUrl(line), url: line })
      continue
    }
    // 第一个冒号分割：左侧是 name，右侧是 url。用 indexOf 而不是回溯正则，杜绝 ReDoS。
    const colonIdx = line.indexOf(':')
    if (colonIdx > 0) {
      const name = line.slice(0, colonIdx).trim()
      const url = line.slice(colonIdx + 1).trim()
      if (name && url && isLikelyUrl(url)) {
        items.push({ name, url })
        continue
      }
    }
    invalid++
  }
  return {
    items,
    invalidLines: invalid,
    reason: items.length > 0 ? 'ok' : 'text-no-valid-lines',
  }
}

// 添加单个表情包
async function handleAddSticker() {
  if (!newStickerName.value.trim() || !newStickerUrl.value.trim()) {
    showError('请填写表情名称和URL')
    return
  }

  const normalized = await normalizeStickerUrlForImport(newStickerUrl.value.trim())
  if (!normalized) {
    showError('图片下载失败，请检查链接是否可访问')
    return
  }

  const result = stickersStore.addStickerToPack({
    name: newStickerName.value.trim(),
    url: normalized,
  }, props.packId)

  if (result) {
    newStickerName.value = ''
    newStickerUrl.value = ''
    addMode.value = 'url'
    showAddDialog.value = false
    showSuccess(`成功添加表情包"${result.name}"`)
  }
  else {
    showError('添加失败：该表情包已存在')
  }
}

// 上传图片（支持多选）
async function handleImageUpload(event: Event) {
  const input = event.target as HTMLInputElement
  const files = input.files
  if (!files || files.length === 0)
    return

  try {
    if (files.length === 1) {
      // 单张：填充到表单让用户确认
      const file = files[0]
      const base64 = await compressStickerImage(file)
      const fileName = file.name.replace(/\.(jpg|jpeg|png|gif|webp)$/i, '')
      newStickerUrl.value = base64
      newStickerName.value = fileName

      if (isVisionAvailable.value) {
        isAutoNaming.value = true
        try {
          const existingNames = contactStickers.value.map(s => s.name)
          const name = await autoName(base64, existingNames)
          if (name)
            newStickerName.value = name
        }
        finally {
          isAutoNaming.value = false
        }
      }
    }
    else {
      // 多张：直接批量添加
      isImporting.value = true
      importResult.value = null
      const stickersData: Array<{ name: string, url: string }> = []

      // 初始化进度追踪
      const fileArray = Array.from(files)
      processingTotal.value = fileArray.length
      processingProgress.value = 0

      for (const file of fileArray) {
        try {
          const base64 = await compressStickerImage(file)
          const fileName = file.name.replace(/\.(jpg|jpeg|png|gif|webp)$/i, '')
          stickersData.push({ name: fileName, url: base64 })
        }
        catch {
          console.warn('跳过无法读取的文件:', file.name)
        }
        finally {
          processingProgress.value++
        }
      }

      // 重置进度
      processingProgress.value = 0
      processingTotal.value = 0

      if (stickersData.length > 0) {
        if (isVisionAvailable.value && enableAiNaming.value) {
          const existingNames = contactStickers.value.map(s => s.name)
          const results = await batchAutoName(
            stickersData.map(s => ({ url: s.url, fallbackName: s.name })),
            existingNames,
          )
          for (const r of results) {
            stickersData[r.index].name = r.name
          }
        }
        const result = stickersStore.bulkAddStickersToPack(stickersData, props.packId)
        importResult.value = result
        if (result.success > 0) {
          showSuccess(`成功添加 ${result.success} 个表情包`)
        }
      }
      isImporting.value = false
    }
  }
  catch (error) {
    console.error('图片上传失败:', error)
    showError('图片上传失败，请重试')
    isImporting.value = false
  }
  finally {
    input.value = ''
  }
}

// 上传压缩包（批量）
async function handleZipUpload(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file)
    return

  isImporting.value = true
  importResult.value = null
  uploadedZipFileName.value = file.name

  try {
    // 动态导入 JSZip
    const JSZip = (await import('jszip')).default
    const zip = new JSZip()

    // 解压 ZIP 文件
    const zipData = await zip.loadAsync(file)

    // 提取所有图片文件
    const imageFiles: Array<{ name: string, url: string }> = []

    // 统计图片文件数量
    const imageEntries = Object.entries(zipData.files).filter(([fileName, zipEntry]) =>
      !zipEntry.dir && /\.(?:jpg|jpeg|png|gif|webp)$/i.test(fileName),
    )

    // 初始化进度追踪
    processingTotal.value = imageEntries.length
    processingProgress.value = 0

    for (const [fileName, zipEntry] of imageEntries) {
      try {
        // 读取文件为 blob
        const blob = await zipEntry.async('blob')

        // 压缩并转换为 base64
        const base64 = await compressStickerImage(blob, fileName)

        // 从路径中提取文件名（去除路径和扩展名）
        const name = fileName.split('/').pop()?.replace(/\.(jpg|jpeg|png|gif|webp)$/i, '') || fileName

        imageFiles.push({ name, url: base64 })
      }
      catch (err) {
        console.error(`处理文件 ${fileName} 失败:`, err)
      }
      finally {
        processingProgress.value++
      }
    }

    // 重置进度
    processingProgress.value = 0
    processingTotal.value = 0

    if (imageFiles.length === 0) {
      showError('压缩包中没有找到图片文件')
      return
    }

    // Vision 自动命名
    if (isVisionAvailable.value && enableAiNaming.value) {
      const existingNames = contactStickers.value.map(s => s.name)
      const results = await batchAutoName(
        imageFiles.map(s => ({ url: s.url, fallbackName: s.name })),
        existingNames,
      )
      for (const r of results) {
        imageFiles[r.index].name = r.name
      }
    }

    // 批量添加
    const result = stickersStore.bulkAddStickersToPack(imageFiles, props.packId)
    importResult.value = result

    if (result.success > 0)
      showSuccess(`成功导入 ${result.success} 个表情包！`)

    setTimeout(() => {
      showBulkImportDialog.value = false
      importResult.value = null
      bulkMode.value = 'text'
    }, 2000)
  }
  catch (error) {
    console.error('压缩包解析失败:', error)
    showError(`压缩包解析失败: ${(error as Error).message}`)
  }
  finally {
    isImporting.value = false
    // 清空 input
    input.value = ''
  }
}

// 工具函数：文件转 Base64
function fileToBase64(file: File | Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

// 导入时压缩表情包图片（GIF 强制转换为 WebP，AI 模型不支持 GIF）
async function compressStickerImage(file: File | Blob, fileName?: string): Promise<string> {
  const name = fileName || (file instanceof File ? file.name : 'sticker.png')
  const isGif = /\.gif$/i.test(name) || file.type === 'image/gif'

  if (!isGif && file.size <= STICKER_COMPRESS_THRESHOLD)
    return fileToBase64(file)

  try {
    const target = file instanceof File ? file : new File([file], name, { type: file.type || 'image/png' })
    const compressed = await imageCompression(target, STICKER_COMPRESSION_OPTIONS)
    return fileToBase64(compressed)
  }
  catch {
    return fileToBase64(file)
  }
}

// 删除表情包
function handleDelete(stickerId: string) {
  deletingStickerId.value = stickerId
  showDeleteConfirm.value = true
}

function confirmDelete() {
  // 先保存 ID，避免被重置
  const idToDelete = deletingStickerId.value

  // 立即关闭对话框和重置状态
  showDeleteConfirm.value = false
  deletingStickerId.value = null

  // 执行删除
  if (idToDelete) {
    const sticker = contactStickers.value.find(s => s.id === idToDelete)
    const deleted = stickersStore.deleteStickerFromPack(idToDelete, props.packId)
    if (deleted && sticker) {
      showSuccess(`已删除表情包"${sticker.name}"`)
    }
  }
}

// 批量删除
function handleBatchDelete() {
  if (selectedStickers.value.size === 0)
    return

  showBatchDeleteConfirm.value = true
}

function confirmBatchDelete() {
  const count = selectedStickers.value.size
  const idsToDelete = Array.from(selectedStickers.value)

  // 立即关闭对话框和重置状态
  showBatchDeleteConfirm.value = false
  selectedStickers.value.clear()
  isEditMode.value = false

  // 执行删除
  for (const id of idsToDelete) {
    stickersStore.deleteStickerFromPack(id, props.packId)
  }
  showSuccess(`已删除 ${count} 个表情包`)
}

// 导出
function handleExport(format: 'json' | 'text') {
  const content = format === 'json'
    ? stickersStore.exportPackAsJson(props.packId)
    : stickersStore.exportPackAsText(props.packId)

  // 创建下载
  const blob = new Blob([content], { type: format === 'json' ? 'application/json' : 'text/plain' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `stickers_${props.packId}_${Date.now()}.${format === 'json' ? 'json' : 'txt'}`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)

  showExportDialog.value = false
}

// 切换选择
function toggleSelect(stickerId: string) {
  if (selectedStickers.value.has(stickerId)) {
    selectedStickers.value.delete(stickerId)
  }
  else {
    selectedStickers.value.add(stickerId)
  }
}

// 切换编辑模式
function toggleEditMode() {
  isEditMode.value = !isEditMode.value
  if (!isEditMode.value) {
    selectedStickers.value.clear()
  }
}

// 重命名表情包
function startRename(stickerId: string) {
  const sticker = contactStickers.value.find(s => s.id === stickerId)
  if (sticker) {
    renamingSticker.value = { id: sticker.id, name: sticker.name }
    showRenameDialog.value = true
  }
}

function handleRename() {
  if (!renamingSticker.value || !renamingSticker.value.name.trim()) {
    showError('请输入表情包名称')
    return
  }

  const sticker = contactStickers.value.find(s => s.id === renamingSticker.value!.id)
  if (sticker) {
    const oldName = sticker.name
    sticker.name = renamingSticker.value.name.trim()
    showRenameDialog.value = false
    renamingSticker.value = null
    showSuccess(`已将"${oldName}"重命名为"${sticker.name}"`)
  }
}

// 批量智能重命名
async function handleBatchVisionRename() {
  if (!isVisionAvailable.value) {
    showError('请先配置聊天服务商')
    return
  }

  const pack = stickersStore.getPackById(props.packId)
  if (!pack || pack.stickers.length === 0)
    return

  const stickersToRename = pack.stickers
  const existingNames: string[] = []

  isImporting.value = true
  try {
    const results = await batchAutoName(
      stickersToRename.map(s => ({ url: s.url, fallbackName: s.name })),
      existingNames,
    )

    let renamed = 0
    for (const r of results) {
      if (r.success) {
        stickersStore.updateStickerInPack(stickersToRename[r.index].id, { name: r.name }, props.packId)
        renamed++
      }
    }

    if (renamed > 0)
      showSuccess(`已智能命名 ${renamed} 个表情包`)
    else if (!visionError.value)
      showError('智能命名失败，请检查模型是否支持识图')
  }
  catch (err) {
    showError(`智能命名失败: ${(err as Error).message}`)
  }
  finally {
    isImporting.value = false
  }
}
</script>

<template>
  <div class="sticker-management" p-4>
    <!-- 隐藏的文件上传 input -->
    <input
      ref="imageFileInput"
      type="file"
      accept="image/jpeg,image/png,image/gif,image/webp"
      multiple
      hidden
      @change="handleImageUpload"
    >
    <input
      ref="zipFileInput"
      type="file"
      accept=".zip"
      hidden
      @change="handleZipUpload"
    >
    <!-- 头部操作栏 -->
    <div class="header" mb-4 flex items-center justify-between>
      <div>
        <h3 text-lg>
          表情包管理
        </h3>
        <p text-xs text-neutral-500 dark:text-neutral-400>
          共 {{ contactStickers.length }} 个表情
        </p>
      </div>
      <div flex items-center gap-2>
        <template v-if="!isEditMode">
          <button
            rounded bg-primary-500 px-4 py-2 text-sm text-white
            @click="showAddDialog = true"
          >
            添加
          </button>
          <button
            rounded bg-blue-500 px-4 py-2 text-sm text-white
            @click="showBulkImportDialog = true"
          >
            批量导入
          </button>
          <!-- 更多菜单 -->
          <div v-if="contactStickers.length > 0" relative>
            <button
              rounded bg-neutral-200 px-3 py-2 text-sm text-neutral-700 dark:bg-neutral-700 dark:text-neutral-300
              @click="showMoreMenu = !showMoreMenu"
            >
              <div i-lucide:more-horizontal h-4 w-4 />
            </button>
            <div
              v-if="showMoreMenu"
              absolute right-0 top-full z-10 mt-1 min-w-36 rounded-lg bg-white py-1 shadow-lg ring-1 ring-neutral-200 dark:bg-neutral-800 dark:ring-neutral-700
            >
              <button
                w-full px-4 py-2 text-left text-sm text-neutral-700 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-700
                @click="showExportDialog = true; showMoreMenu = false"
              >
                <div flex items-center gap-2>
                  <div i-lucide:download h-4 w-4 />
                  导出
                </div>
              </button>
              <button
                v-if="isVisionAvailable"
                w-full px-4 py-2 text-left text-sm text-neutral-700 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-700
                :disabled="isNaming || isImporting"
                @click="handleBatchVisionRename(); showMoreMenu = false"
              >
                <div flex items-center gap-2>
                  <div i-lucide:sparkles h-4 w-4 />
                  {{ isNaming ? `命名中 ${namingProgress}/${namingTotal}` : '智能命名' }}
                </div>
              </button>
            </div>
            <!-- 点击外部关闭菜单 -->
            <div v-if="showMoreMenu" fixed inset-0 z-9 @click="showMoreMenu = false" />
          </div>
        </template>
        <button
          v-if="contactStickers.length > 0"
          rounded bg-neutral-200 px-4 py-2 text-sm text-neutral-700 dark:bg-neutral-700 dark:text-neutral-300
          @click="toggleEditMode"
        >
          {{ isEditMode ? '完成' : '编辑' }}
        </button>
      </div>
    </div>

    <!-- 文件处理进度 -->
    <div v-if="isProcessing" class="dark:bg-blue-900/20" mb-4 rounded bg-blue-50 p-3>
      <div mb-1 flex items-center gap-2 text-sm text-blue-700 dark:text-blue-300>
        <div i-lucide:loader-2 animate-spin />
        <span>正在处理文件... {{ processingProgress }}/{{ processingTotal }} ({{ processingTotal > 0 ? Math.round(processingProgress / processingTotal * 100) : 0 }}%)</span>
      </div>
      <div h-1.5 w-full overflow-hidden rounded-full bg-blue-200 dark:bg-blue-800>
        <div
          h-full rounded-full bg-blue-500 transition-all
          :style="{ width: `${processingTotal > 0 ? (processingProgress / processingTotal) * 100 : 0}%` }"
        />
      </div>
    </div>

    <!-- AI 命名进度 -->
    <div v-if="isNaming" class="dark:bg-amber-900/20" mb-4 rounded bg-amber-50 p-3>
      <div mb-1 flex items-center justify-between gap-2>
        <div flex items-center gap-2 text-sm text-amber-700 dark:text-amber-300>
          <div i-lucide:loader-2 animate-spin />
          <span>AI 正在识别表情包内容... {{ namingProgress }}/{{ namingTotal }} ({{ namingTotal > 0 ? Math.round(namingProgress / namingTotal * 100) : 0 }}%)</span>
        </div>
        <button
          rounded bg-amber-200 px-2 py-0.5 text-xs text-amber-700 dark:bg-amber-800 hover:bg-amber-300 dark:text-amber-300 dark:hover:bg-amber-700
          @click="cancelNaming"
        >
          取消
        </button>
      </div>
      <div h-1.5 w-full overflow-hidden rounded-full bg-amber-200 dark:bg-amber-800>
        <div
          h-full rounded-full bg-amber-500 transition-all
          :style="{ width: `${namingTotal > 0 ? (namingProgress / namingTotal) * 100 : 0}%` }"
        />
      </div>
    </div>
    <div v-if="visionError" class="dark:bg-red-900/20" mb-4 rounded bg-red-50 p-3 text-sm text-red-600 dark:text-red-400>
      {{ visionError }}
    </div>
    <div v-if="isEditMode && selectedStickers.size > 0" class="dark:bg-blue-900/20" mb-4 flex items-center gap-2 rounded bg-blue-50 p-2>
      <span text-sm text-neutral-700 dark:text-neutral-300>已选择 {{ selectedStickers.size }} 个</span>
      <Button
        variant="danger"
        size="sm"
        shape="rounded"
        @click="handleBatchDelete"
      >
        删除选中
      </Button>
    </div>

    <!-- 表情包网格 -->
    <div v-if="contactStickers.length > 0" class="sticker-grid" grid="~ cols-2 gap-4 md:cols-3 lg:cols-4">
      <div
        v-for="sticker in contactStickers"
        :key="sticker.id"
        class="sticker-item"

        relative aspect-square cursor-pointer rounded-lg bg-neutral-100 p-2 transition dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700
        :class="{ 'ring-2 ring-blue-500': selectedStickers.has(sticker.id) }"
        @click="isEditMode ? toggleSelect(sticker.id) : null"
      >
        <img
          :src="sticker.url"
          :alt="sticker.name"
          class="h-full w-full object-contain"
        >
        <div
          absolute bottom-0 left-0 right-0 truncate bg-black bg-opacity-50 p-2 text-center text-sm text-white
        >
          {{ sticker.name }}
        </div>
        <!-- 编辑模式复选框 -->
        <div
          v-if="isEditMode"
          class="checkbox"
          absolute right-2 top-2 h-6 w-6 border-2 border-white rounded bg-blue-500
          :class="selectedStickers.has(sticker.id) ? 'opacity-100' : 'opacity-0'"
        />
        <!-- 非编辑模式操作按钮 -->
        <div v-else class="actions" absolute right-2 top-2 flex gap-2>
          <Button
            variant="primary"
            size="sm"
            shape="rounded"
            class="shadow-lg"
            @click.stop="startRename(sticker.id)"
          >
            <div i-lucide:edit-3 h-5 w-5 />
          </Button>
          <Button
            variant="danger"
            size="sm"
            shape="rounded"
            class="shadow-lg"
            @click.stop="handleDelete(sticker.id)"
          >
            <div i-lucide:trash-2 h-5 w-5 />
          </Button>
        </div>
      </div>
    </div>

    <!-- 空状态 -->
    <div v-else class="empty-state" flex="~ col" items-center justify-center py-12 text-neutral-400 dark:text-neutral-500>
      <div i-lucide:image-plus mb-4 text-6xl />
      <p text-sm>
        还没有表情包，点击"添加"或"批量导入"开始吧！
      </p>
    </div>

    <!-- 添加对话框 -->
    <div v-if="showAddDialog" class="modal" fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4 @click.self="closeAddDialog">
      <div class="modal-content max-h-[calc(100vh-2rem)]" max-w-md w-full overflow-y-auto rounded-lg bg-white p-6 shadow-xl dark:bg-neutral-800>
        <div mb-4 flex items-center justify-between>
          <h3 text-lg text-neutral-900 dark:text-neutral-100>
            添加表情包
          </h3>
          <button
            rounded p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600 dark:hover:bg-neutral-700 dark:hover:text-neutral-300
            @click="closeAddDialog"
          >
            <div i-lucide:x h-5 w-5 />
          </button>
        </div>

        <!-- 选择添加方式 -->
        <div mb-4 flex gap-2>
          <button
            flex-1 border-2 rounded px-4 py-2 text-sm
            :class="addMode === 'url' ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300' : 'border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-300'"
            @click="addMode = 'url'"
          >
            URL 导入
          </button>
          <button
            flex-1 border-2 rounded px-4 py-2 text-sm
            :class="addMode === 'image' ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300' : 'border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-300'"
            @click="addMode = 'image'"
          >
            图片上传
          </button>
        </div>

        <!-- URL 模式 -->
        <div v-if="addMode === 'url'">
          <div mb-4>
            <label mb-2 block text-sm text-neutral-700 dark:text-neutral-300>表情名称</label>
            <input
              v-model="newStickerName"
              type="text"
              placeholder="例如：开心、疑惑、赞"
              w-full border border-neutral-300 rounded bg-white px-3 py-2.5 text-base text-neutral-900 dark:border-neutral-600 dark:bg-neutral-900 dark:text-neutral-100
            >
          </div>
          <div mb-4>
            <label mb-2 block text-sm text-neutral-700 dark:text-neutral-300>图片 URL</label>
            <input
              v-model="newStickerUrl"
              type="text"
              placeholder="https://example.com/image.png"
              w-full border border-neutral-300 rounded bg-white px-3 py-2.5 text-base text-neutral-900 dark:border-neutral-600 dark:bg-neutral-900 dark:text-neutral-100
            >
          </div>
        </div>

        <!-- 图片上传模式 -->
        <div v-else>
          <div mb-4>
            <label mb-2 block text-sm text-neutral-700 dark:text-neutral-300>选择图片</label>
            <button
              class="dark:hover:bg-primary-900/20" w-full border-2 border-neutral-300 rounded-lg border-dashed p-8 text-center dark:border-neutral-600 hover:border-primary-500 hover:bg-primary-50
              @click="imageFileInput?.click()"
            >
              <div i-lucide:upload mb-2 text-4xl text-neutral-400 dark:text-neutral-500 />
              <p text-sm text-neutral-600 dark:text-neutral-400>
                点击上传图片
              </p>
              <p text-xs text-neutral-400 dark:text-neutral-500>
                支持 JPG、PNG、GIF、WebP
              </p>
            </button>
          </div>
          <div v-if="isVisionAvailable" class="dark:bg-amber-900/20" mb-4 flex items-center justify-between rounded-lg bg-amber-50 px-3 py-2>
            <div flex items-center gap-2 text-sm text-amber-700 dark:text-amber-300>
              <span i-lucide:sparkles />
              <span>AI 智能命名</span>
              <span text-xs text-amber-500 dark:text-amber-400>（会消耗 token）</span>
            </div>
            <button
              h-6 w-11 rounded-full transition-colors
              :class="enableAiNaming ? 'bg-amber-500' : 'bg-neutral-300 dark:bg-neutral-600'"
              @click="enableAiNaming = !enableAiNaming"
            >
              <div
                h-5 w-5 rounded-full bg-white shadow transition-transform
                :class="enableAiNaming ? 'translate-x-5.5' : 'translate-x-0.5'"
              />
            </button>
          </div>

          <!-- 多图上传 AI 命名开关 -->
          <div v-if="newStickerUrl" mb-4>
            <label mb-2 block text-sm text-neutral-700 dark:text-neutral-300>表情名称</label>
            <div relative>
              <input
                v-model="newStickerName"
                type="text"
                placeholder="例如：开心、疑惑、赞"
                w-full border border-neutral-300 rounded bg-white px-3 py-2.5 text-base text-neutral-900 dark:border-neutral-600 dark:bg-neutral-900 dark:text-neutral-100
                :disabled="isAutoNaming"
              >
              <div v-if="isAutoNaming" absolute inset-y-0 right-3 flex items-center>
                <div i-lucide:loader-2 animate-spin text-neutral-400 />
              </div>
            </div>
            <p v-if="isAutoNaming" mt-1 text-xs text-amber-500>
              AI 正在识别表情内容...
            </p>
          </div>
          <div v-if="newStickerUrl" mb-4>
            <label mb-2 block text-sm text-neutral-700 dark:text-neutral-300>预览</label>
            <img :src="newStickerUrl" alt="预览" max-h-40 border border-neutral-200 rounded object-contain dark:border-neutral-700>
          </div>
        </div>

        <div flex justify-end gap-2>
          <button
            rounded bg-neutral-100 px-5 py-2.5 text-base text-neutral-700 dark:bg-neutral-700 hover:bg-neutral-200 dark:text-neutral-200 dark:hover:bg-neutral-600
            @click="closeAddDialog"
          >
            取消
          </button>
          <button
            rounded bg-primary-500 px-5 py-2.5 text-base text-white
            :disabled="!newStickerName.trim() || !newStickerUrl.trim()"
            @click="handleAddSticker"
          >
            添加
          </button>
        </div>
      </div>
    </div>

    <!-- 批量导入对话框 -->
    <div v-if="showBulkImportDialog" class="modal" fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4 @click.self="closeBulkImportDialog">
      <div class="modal-content max-h-[calc(100vh-2rem)]" max-w-2xl w-full overflow-y-auto rounded-lg bg-white p-6 shadow-xl dark:bg-neutral-800>
        <div mb-4 flex items-center justify-between>
          <h3 text-lg text-neutral-900 dark:text-neutral-100>
            批量导入表情包
          </h3>
          <button
            rounded p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600 dark:hover:bg-neutral-700 dark:hover:text-neutral-300
            :disabled="isImporting || isNaming"
            @click="closeBulkImportDialog"
          >
            <div i-lucide:x h-5 w-5 />
          </button>
        </div>

        <!-- 选择导入方式 -->
        <div mb-4 flex gap-2>
          <button
            flex-1 border-2 rounded px-4 py-2 text-sm
            :class="bulkMode === 'text' ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' : 'border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-300'"
            @click="bulkMode = 'text'"
          >
            文本 / JSON
          </button>
          <button
            flex-1 border-2 rounded px-4 py-2 text-sm
            :class="bulkMode === 'zip' ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' : 'border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-300'"
            @click="bulkMode = 'zip'"
          >
            ZIP 压缩包
          </button>
        </div>

        <!-- 文本模式 -->
        <div v-if="bulkMode === 'text'">
          <div mb-3 rounded-lg bg-neutral-50 p-3 dark:bg-neutral-900>
            <p mb-2 text-xs text-neutral-500 font-medium dark:text-neutral-400>
              支持两种格式（每行一条）：
            </p>
            <div mb-1.5 rounded bg-white px-2.5 py-1.5 text-xs text-neutral-600 font-mono ring-1 ring-neutral-200 dark:bg-neutral-800 dark:text-neutral-300 dark:ring-neutral-700>
              开心: https://example.com/happy.png
            </div>
            <div rounded bg-white px-2.5 py-1.5 text-xs text-neutral-600 font-mono ring-1 ring-neutral-200 dark:bg-neutral-800 dark:text-neutral-300 dark:ring-neutral-700>
              [{"name":"开心","url":"https://..."}]
            </div>
          </div>
          <div mb-4>
            <textarea
              v-model="importInput"
              placeholder="粘贴表情包数据..."
              h-48 w-full border border-neutral-300 rounded bg-white px-3 py-2.5 text-base text-neutral-900 font-mono dark:border-neutral-600 dark:bg-neutral-900 dark:text-neutral-100
            />
          </div>
        </div>

        <!-- ZIP 模式 -->
        <div v-else>
          <!-- AI 命名开关 -->
          <div v-if="isVisionAvailable" class="dark:bg-amber-900/20" mb-4 flex items-center justify-between rounded-lg bg-amber-50 px-3 py-2>
            <div flex items-center gap-2 text-sm text-amber-700 dark:text-amber-300>
              <span i-lucide:sparkles />
              <span>AI 智能命名</span>
              <span text-xs text-amber-500 dark:text-amber-400>（会消耗 token）</span>
            </div>
            <button
              h-6 w-11 rounded-full transition-colors
              :class="enableAiNaming ? 'bg-amber-500' : 'bg-neutral-300 dark:bg-neutral-600'"
              @click="enableAiNaming = !enableAiNaming"
            >
              <div
                h-5 w-5 rounded-full bg-white shadow transition-transform
                :class="enableAiNaming ? 'translate-x-5.5' : 'translate-x-0.5'"
              />
            </button>
          </div>

          <!-- ZIP 处理进度 -->
          <div v-if="isProcessing || isNaming" mb-4 flex="~ col" gap-2>
            <div v-if="isProcessing" class="dark:bg-blue-900/20" rounded bg-blue-50 p-3>
              <div mb-1 flex items-center gap-2 text-sm text-blue-700 dark:text-blue-300>
                <div i-lucide:loader-2 animate-spin />
                <span>正在解压文件... {{ processingProgress }}/{{ processingTotal }} ({{ processingTotal > 0 ? Math.round(processingProgress / processingTotal * 100) : 0 }}%)</span>
              </div>
              <div h-1.5 w-full overflow-hidden rounded-full bg-blue-200 dark:bg-blue-800>
                <div
                  h-full rounded-full bg-blue-500 transition-all
                  :style="{ width: `${processingTotal > 0 ? (processingProgress / processingTotal) * 100 : 0}%` }"
                />
              </div>
            </div>
            <div v-if="isNaming" class="dark:bg-amber-900/20" rounded bg-amber-50 p-3>
              <div mb-1 flex items-center justify-between gap-2>
                <div flex items-center gap-2 text-sm text-amber-700 dark:text-amber-300>
                  <div i-lucide:loader-2 animate-spin />
                  <span>AI 识别中... {{ namingProgress }}/{{ namingTotal }} ({{ namingTotal > 0 ? Math.round(namingProgress / namingTotal * 100) : 0 }}%)</span>
                </div>
                <button
                  rounded bg-amber-200 px-2 py-0.5 text-xs text-amber-700 dark:bg-amber-800 hover:bg-amber-300 dark:text-amber-300 dark:hover:bg-amber-700
                  @click="cancelNaming"
                >
                  取消
                </button>
              </div>
              <div h-1.5 w-full overflow-hidden rounded-full bg-amber-200 dark:bg-amber-800>
                <div
                  h-full rounded-full bg-amber-500 transition-all
                  :style="{ width: `${namingTotal > 0 ? (namingProgress / namingTotal) * 100 : 0}%` }"
                />
              </div>
            </div>
          </div>

          <!-- 上传区域：未选文件时显示拖拽区，已选文件时显示文件信息 -->
          <div v-if="!uploadedZipFileName" mb-4>
            <button
              class="dark:hover:bg-blue-900/20" w-full border-2 border-neutral-300 rounded-lg border-dashed p-12 text-center dark:border-neutral-600 hover:border-blue-500 hover:bg-blue-50
              @click="zipFileInput?.click()"
            >
              <div i-lucide:file-archive mb-3 text-5xl text-neutral-400 dark:text-neutral-500 />
              <p text-base text-neutral-600 dark:text-neutral-400>
                点击上传 ZIP 文件
              </p>
              <p text-sm text-neutral-400 dark:text-neutral-500>
                支持 JPG、PNG、GIF、WebP 图片
              </p>
            </button>
          </div>
          <div v-else mb-4 flex items-center gap-3 rounded-lg bg-neutral-50 p-3 ring-1 ring-neutral-200 dark:bg-neutral-900 dark:ring-neutral-700>
            <div i-lucide:file-archive shrink-0 text-2xl text-blue-500 />
            <div min-w-0 flex-1>
              <p truncate text-sm text-neutral-800 dark:text-neutral-200>
                {{ uploadedZipFileName }}
              </p>
              <p text-xs text-neutral-400 dark:text-neutral-500>
                {{ isImporting || isNaming ? '处理中...' : '处理完成' }}
              </p>
            </div>
            <button
              v-if="!isImporting && !isNaming"
              rounded p-1 text-neutral-400 hover:bg-neutral-200 hover:text-neutral-600 dark:hover:bg-neutral-700
              @click="uploadedZipFileName = null; zipFileInput && (zipFileInput.value = '')"
            >
              <div i-lucide:x h-4 w-4 />
            </button>
          </div>
        </div>

        <!-- 解析问题提示（格式错误 / 部分无效行 / 取消） -->
        <div
          v-if="importParseIssue"
          class="dark:bg-amber-900/20"
          mb-4 flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:text-amber-300
        >
          <span i-solar:info-circle-bold-duotone mt-0.5 shrink-0 />
          <span>{{ importParseIssue }}</span>
        </div>

        <!-- 导入结果 -->
        <div v-if="importResult" mb-4 rounded-lg p-4 text-sm ring-1 ring-neutral-200 dark:ring-neutral-700>
          <p v-if="importResult.success > 0" flex items-center gap-1.5 text-green-600 dark:text-green-400>
            <span i-solar:check-circle-bold-duotone />
            成功导入 {{ importResult.success }} 个
          </p>
          <p v-if="importResult.skipped > 0" flex items-center gap-1.5 text-yellow-600 dark:text-yellow-400>
            <span i-solar:danger-triangle-bold-duotone />
            跳过 {{ importResult.skipped }} 个（已存在）
          </p>
          <p v-if="importResult.failed > 0" flex items-center gap-1.5 text-red-600 dark:text-red-400>
            <span i-solar:close-circle-bold-duotone />
            失败 {{ importResult.failed }} 个
          </p>
        </div>

        <div flex justify-end gap-2>
          <Button
            variant="secondary"
            size="sm"
            shape="rounded"
            :disabled="isImporting || isNaming"
            @click="closeBulkImportDialog"
          >
            关闭
          </Button>
          <Button
            v-if="bulkMode === 'text' && isImporting"
            variant="danger"
            size="sm"
            shape="rounded"
            @click="cancelImport"
          >
            取消导入
          </Button>
          <Button
            v-if="bulkMode === 'text' && !isImporting"
            variant="primary"
            size="sm"
            shape="rounded"
            :disabled="!importInput.trim()"
            @click="handleBulkImport"
          >
            开始导入
          </Button>
          <Button
            v-else-if="bulkMode === 'zip' && !uploadedZipFileName && !isImporting"
            variant="primary"
            size="sm"
            shape="rounded"
            @click="zipFileInput?.click()"
          >
            选择文件
          </Button>
        </div>
      </div>
    </div>

    <!-- 导出对话框 -->
    <div v-if="showExportDialog" class="modal" fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4 @click.self="showExportDialog = false">
      <div class="modal-content" max-w-sm w-full rounded-lg bg-white p-6 shadow-xl dark:bg-neutral-800>
        <div mb-4 flex items-center justify-between>
          <h3 text-lg text-neutral-900 dark:text-neutral-100>
            导出表情包
          </h3>
          <button
            rounded p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600 dark:hover:bg-neutral-700 dark:hover:text-neutral-300
            @click="showExportDialog = false"
          >
            <div i-lucide:x h-5 w-5 />
          </button>
        </div>
        <p mb-4 text-sm text-neutral-600 dark:text-neutral-400>
          选择导出格式：
        </p>
        <div flex="~ col" mb-4 gap-3>
          <button
            w-full rounded bg-blue-500 px-5 py-3.5 text-base text-white
            @click="handleExport('json')"
          >
            JSON 格式
          </button>
          <button
            w-full rounded bg-green-500 px-5 py-3.5 text-base text-white
            @click="handleExport('text')"
          >
            纯文本格式
          </button>
        </div>
        <button
          w-full rounded bg-neutral-100 px-5 py-2.5 text-base text-neutral-700 dark:bg-neutral-700 hover:bg-neutral-200 dark:text-neutral-200 dark:hover:bg-neutral-600
          @click="showExportDialog = false"
        >
          取消
        </button>
      </div>
    </div>

    <!-- 重命名对话框 -->
    <div v-if="showRenameDialog && renamingSticker" class="modal" fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4 @click.self="showRenameDialog = false; renamingSticker = null">
      <div class="modal-content" max-w-sm w-full rounded-lg bg-white p-6 shadow-xl dark:bg-neutral-800>
        <div mb-4 flex items-center justify-between>
          <h3 text-lg text-neutral-900 dark:text-neutral-100>
            重命名表情包
          </h3>
          <button
            rounded p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600 dark:hover:bg-neutral-700 dark:hover:text-neutral-300
            @click="showRenameDialog = false; renamingSticker = null"
          >
            <div i-lucide:x h-5 w-5 />
          </button>
        </div>
        <div mb-4>
          <label mb-2 block text-sm text-neutral-700 dark:text-neutral-300>新名称</label>
          <input
            v-model="renamingSticker.name"
            type="text"
            placeholder="输入新的表情包名称"
            w-full border border-neutral-300 rounded bg-white px-3 py-2.5 text-base text-neutral-900 dark:border-neutral-600 dark:bg-neutral-900 dark:text-neutral-100
            @keyup.enter="handleRename"
          >
        </div>
        <div flex justify-end gap-2>
          <button
            rounded bg-neutral-100 px-5 py-2.5 text-base text-neutral-700 dark:bg-neutral-700 hover:bg-neutral-200 dark:text-neutral-200 dark:hover:bg-neutral-600
            @click="showRenameDialog = false; renamingSticker = null"
          >
            取消
          </button>
          <button
            rounded bg-primary-500 px-5 py-2.5 text-base text-white
            @click="handleRename"
          >
            确定
          </button>
        </div>
      </div>
    </div>

    <!-- 删除确认对话框 -->
    <ConfirmDialog
      :show="showDeleteConfirm"
      title="删除表情包"
      message="确定要删除这个表情包吗？"
      confirm-text="删除"
      cancel-text="取消"
      @confirm="confirmDelete"
      @cancel="showDeleteConfirm = false"
    />

    <!-- 批量删除确认对话框 -->
    <ConfirmDialog
      :show="showBatchDeleteConfirm"
      title="批量删除"
      :message="`确定要删除选中的 ${selectedStickers.size} 个表情包吗？`"
      confirm-text="删除"
      cancel-text="取消"
      @confirm="confirmBatchDelete"
      @cancel="showBatchDeleteConfirm = false"
    />
  </div>
</template>

<style scoped>
.sticker-item {
 --at-apply: group;
}

.sticker-item:hover .actions {
 opacity: 1;
}
</style>
